/**
 * "X is typing" for one channel.
 *
 * Listens for `typing.changed` on the relay channel the conversation hangs off (its
 * order, or its home) — the channel a whole order's threads share, so an event is
 * matched to THIS conversation by its `channelId`, never by the relay channel alone; an
 * unmatched indicator would show the internal-notes typists inside the homeowner thread.
 *
 * Own events are dropped: the composer announces the viewer's typing, and the echo would
 * show the viewer typing to themselves.
 *
 * The event names the typist by id only. Their name and avatar come from the channel's
 * members, read only while someone is typing, and otherwise from `/users/list` by id.
 * Being allowed to type does not make someone a member: a row is written only when they
 * post or mark the thread read, and the Office Inbox and order chat never mark read on
 * view. So a typist the members lack is a normal case, not a stale cache.
 *
 * A stop is best-effort: a typist who force-closes the tab never sends one.
 * `TYPING_TTL_MS` is the backstop that clears them regardless. The channel `key` in
 * `ChannelView` still remounts this component on channel change, so no event from the
 * old channel can survive the switch even within the TTL window.
 */

import { useGetAuthMe, useGetUsersList } from "@birdwatch/client";
import { isRealtimeChannelAllowed } from "@birdwatch/common";
import { keepPreviousData } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { UserTyping } from "@/components/ui/message";
import { useRealtimeRelay } from "@/features/shared/realtime-relay";
import type { Channel } from "../_api/channel-endpoints";
import { useChannelMembers } from "../_api/use-channel";

/**
 * A typist with no fresh event for this long is dropped. The sender re-announces typing
 * at most every 3s while still typing (`useTypingStatus`), so a continuously-typing user
 * never expires; one who walks off mid-message clears even though no stop came.
 */
const TYPING_TTL_MS = 8_000;

/** How often expired typists are swept. Only runs while someone is typing. */
const TYPING_SWEEP_MS = 1_000;

/** Typist `userId` → when they stop counting as typing (epoch ms). */
type Typists = Map<string, number>;

/** Pure — returns the SAME reference when nothing expired, so `setState` bails out. */
function pruneExpired(entries: Typists, now: number): Typists {
  const live = new Map(
    Array.from(entries).filter(([, expiresAt]) => expiresAt > now),
  );
  return live.size === entries.size ? entries : live;
}

type ChannelTypingProps = {
  cid: string;
  /** The open conversation: `id` matches events, `entityId` is the relay channel. */
  channel: Pick<Channel, "id" | "entityId">;
  className?: string;
};

export function ChannelTyping({ cid, channel, className }: ChannelTypingProps) {
  const [typists, setTypists] = useState<Typists>(new Map());
  const { data: me } = useGetAuthMe().get();

  useRealtimeRelay({
    // A channel the relay would refuse stops every hook sharing the tab's stream.
    channels: isRealtimeChannelAllowed(channel.entityId)
      ? [channel.entityId]
      : [],
    events: ["typing.changed"],
    onData: (payload) => {
      // Read through `payload`, never destructured: the union narrows on `event`.
      if (payload.event !== "typing.changed") return;
      const { channelId, userId, typing } = payload.data;
      if (channelId !== channel.id || userId === me?.id) return;

      if (typing) {
        setTypists((previous) =>
          new Map(previous).set(userId, Date.now() + TYPING_TTL_MS),
        );
        return;
      }

      setTypists((previous) => {
        // Same reference when the id was never there — React skips the re-render.
        if (!previous.has(userId)) return previous;
        const next = new Map(previous);
        next.delete(userId);
        return next;
      });
    },
  });

  // Gate on the emptiness boolean, not the map itself — depending on the map would
  // restart the interval on every keystroke event.
  const hasTypists = typists.size > 0;
  useEffect(() => {
    if (!hasTypists) return;
    const timer = setInterval(() => {
      setTypists((previous) => pruneExpired(previous, Date.now()));
    }, TYPING_SWEEP_MS);
    return () => clearInterval(timer);
  }, [hasTypists]);

  const { data: members, isPending: isMembersPending } = useChannelMembers(
    cid,
    hasTypists,
  );
  const membersById = useMemo(
    () =>
      new Map(
        (members ?? []).flatMap((member) =>
          member.user ? [[member.userId, member.user] as const] : [],
        ),
      ),
    [members],
  );

  // Key on ids ONLY. Every TTL refresh rewrites the expiry, so keying on more would
  // remount `UserTyping` on each event and restart the bounce animation.
  const typistIds = Array.from(typists.keys());
  const typistKey = [...typistIds].sort().join(",");

  // Waits for the members, so a typist they do have never costs a users request. A
  // failed members read counts as settled: every typist is then looked up by id.
  const missingKey = !isMembersPending
    ? typistKey
        .split(",")
        .filter((userId) => userId && !membersById.has(userId))
        .join(",")
    : "";
  const { data: missingUsers } = useGetUsersList()
    // The `{ in }` object is load-bearing: the shorthand turns one id into `eq`,
    // which the in-only `ids` filter ignores.
    .filters({ ids: { in: missingKey } })
    .take(missingKey === "" ? 1 : missingKey.split(",").length)
    // The key is the whole set, so a second typist starts a new query; keeping the
    // previous result holds the first typist's avatar while it loads.
    .get({ enabled: missingKey !== "", placeholderData: keepPreviousData });
  const missingById = useMemo(
    () => new Map((missingUsers ?? []).map((user) => [user.id, user] as const)),
    [missingUsers],
  );

  if (!hasTypists) return null;

  return (
    <UserTyping
      key={typistKey}
      users={typistIds.map((userId) => {
        const user = membersById.get(userId) ?? missingById.get(userId);
        return {
          name: user?.name || user?.firstName || "",
          avatarUrl: user?.avatarUrl,
        };
      })}
      className={className}
    />
  );
}
